/**
 * CORE-1439 — куда прокрутить Live-чат.
 *
 * Первая правка (18.09) сняла привязку ко времени: прокрутка перестала
 * зависеть от `setTimeout(…, 50)` и стала решаться по изменению размера
 * содержимого. Открытие чата с историей после этого работало.
 *
 * Возврат QA 21.09 показал, что этого мало: при ПОВТОРНОМ открытии панели
 * чат снова оказывался наверху — `scrollTop 0` при `scrollHeight 1162` и
 * `clientHeight 713`, и сам не исправлялся. Новых сообщений при этом не
 * приходило, поэтому ничто и не подталкивало список вниз.
 *
 * Причина того же класса, что и в первый раз: одноразовый выстрел, который
 * может сработать слишком рано. Панель выезжает поверх видео, и первый замер
 * случается, пока её высота ещё нулевая. `scrollToEnd` в нулевую высоту
 * «удаётся» — и помечает первый показ состоявшимся. Когда высота приходит,
 * повторять уже нечего: содержимое не менялось, менялась только панель.
 *
 * Поэтому решение больше не одноразовое. Пока человек не увёл список вверх
 * сам, чат держится низа — и при изменении содержимого, и при изменении
 * размеров самой панели.
 */

export type ScrollAction =
  /** Встать в конец сразу, без анимации. */
  | 'jump'
  /** Доехать плавно — так ведёт себя пришедшее сообщение. */
  | 'animate'
  /** Человек читает выше: не трогать. */
  | 'none';

/** Что изменилось и заставило пересчитать положение. */
export type ScrollTrigger =
  /** Список подрос или уменьшился: пришло сообщение, дорисовался аватар. */
  | 'content'
  /** Изменились размеры самой панели: открылась, выехала, повернули экран. */
  | 'layout';

export interface ScrollState {
  /** Есть ли что показывать. Пустой список — ещё не история. */
  hasMessages: boolean;
  /** Успели ли уже поставить человека в конец. */
  didInitialScroll: boolean;
  /** Человек у нижнего края списка и не уходил читать выше. */
  isNearBottom: boolean;
  trigger: ScrollTrigger;
}

export function nextScrollAction(state: ScrollState): ScrollAction {
  // Пока сообщений нет, прокручивать нечего — и первый показ состоявшимся
  // считать нельзя: иначе пришедшая следом история поедет анимацией от самого
  // верха, что и выглядит как «чат открылся в начале».
  if (!state.hasMessages) return 'none';

  // Человек сам ушёл читать выше — это единственная причина не трогать список.
  if (state.didInitialScroll && !state.isNearBottom) return 'none';

  // Первый показ и любое изменение размеров панели — прыжком. У панели,
  // которая только что открылась, анимация прокрутки выглядит дёрганьем, а
  // главное — к моменту второго замера ехать уже поздно.
  if (!state.didInitialScroll || state.trigger === 'layout') return 'jump';

  return 'animate';
}

/** Считать ли первый показ состоявшимся после этого действия. */
export function marksInitialScrollDone(action: ScrollAction): boolean {
  return action === 'jump' || action === 'animate';
}
